import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import SearchableSelect from "../SearchableSelect";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, BalanceCheckBanner, useFinancialYears, downloadReportCsv, formatCurrency } from "./reportShared";

/**
 * Phase 8 - Trial Balance (items 3-5).
 *
 * Every touched Ledger's Opening/Period/Closing Debit & Credit, grouped by
 * root type (Assets/Liabilities/Funds/Income/Expenses), with the Difference
 * at every level shown - never hidden even when nonzero (item 4). Clicking
 * an Account opens General Ledger for that same account and date context
 * (item 5), never asking the user to reselect it.
 */
export default function TrialBalancePage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/account-groups");
        setGroups(Array.isArray(res.data) ? res.data : []);
      } catch { setGroups([]); }
    })();
  }, []);
  const groupOptions = groups.map((g) => ({ value: g.id, label: g.name, sublabel: g.rootType }));

  const params = { financialYearId, fromDate, toDate, groupId };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${"/society/accounting/reports/trial-balance"}`, { params });
      setData(res.data);
      if (!financialYearId && res.data?.financialYearId) {
        // Leave the selector on "Current" but keep the resolved range for display.
      }
    } catch (err) {
      toast.error(formatErr(err, "Could not load trial balance"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, fromDate, toDate, groupId]);

  const openLedger = (row) => {
    navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${row.ledgerId}&financialYearId=${financialYearId || ""}&fromDate=${fromDate || data?.fromDate || ""}&toDate=${toDate || data?.toDate || ""}`);
  };

  const rows = data?.rows || [];
  const grouped = rows.reduce((acc, r) => {
    (acc[r.rootType] = acc[r.rootType] || []).push(r);
    return acc;
  }, {});
  const rootOrder = ["ASSET", "LIABILITY", "FUND", "INCOME", "EXPENSE"];
  const rootLabels = { ASSET: "Assets", LIABILITY: "Liabilities", FUND: "Funds & Capital", INCOME: "Income", EXPENSE: "Expenses" };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Trial Balance</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Opening, period and closing Debit/Credit for every account. Click an account to open its General Ledger.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          extra={
            <div className="w-56 space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Group</div>
              <SearchableSelect value={groupId} onChange={setGroupId} options={groupOptions} placeholder="All groups" />
            </div>
          }
          onExport={() => downloadReportCsv("/society/accounting/reports/trial-balance", params, `trial-balance-${data?.fromDate || ""}-to-${data?.toDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : rows.length === 0 ? (
          <EmptyState>No posted accounting activity in this range yet.</EmptyState>
        ) : (
          <>
            <SectionCard>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Group</TableHead>
                      <TableHead className="text-right">Opening Dr</TableHead>
                      <TableHead className="text-right">Opening Cr</TableHead>
                      <TableHead className="text-right">Period Dr</TableHead>
                      <TableHead className="text-right">Period Cr</TableHead>
                      <TableHead className="text-right">Closing Dr</TableHead>
                      <TableHead className="text-right">Closing Cr</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rootOrder.filter((rt) => grouped[rt]?.length).map((rt) => (
                      <Fragment key={rt}>
                        <TableRow className="bg-[#F9FAFB]"><TableCell colSpan={8} className="font-bold text-[#111827]">{rootLabels[rt]}</TableCell></TableRow>
                        {grouped[rt].map((row) => (
                          <TableRow key={row.ledgerId}>
                            <TableCell><button type="button" className="text-left font-semibold text-[#111827] hover:underline" onClick={() => openLedger(row)}>{row.ledgerName}</button></TableCell>
                            <TableCell className="text-[#6B7280]">{row.groupName || "-"}</TableCell>
                            <TableCell className="text-right">{row.openingDebit ? formatCurrency(row.openingDebit) : "—"}</TableCell>
                            <TableCell className="text-right">{row.openingCredit ? formatCurrency(row.openingCredit) : "—"}</TableCell>
                            <TableCell className="text-right">{row.periodDebit ? formatCurrency(row.periodDebit) : "—"}</TableCell>
                            <TableCell className="text-right">{row.periodCredit ? formatCurrency(row.periodCredit) : "—"}</TableCell>
                            <TableCell className="text-right">{row.closingDebit ? formatCurrency(row.closingDebit) : "—"}</TableCell>
                            <TableCell className="text-right">{row.closingCredit ? formatCurrency(row.closingCredit) : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                    <TableRow className="border-t-2 border-[#111827] font-bold">
                      <TableCell colSpan={2}>Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.totals.openingDebit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.totals.openingCredit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.totals.periodDebit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.totals.periodCredit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.totals.closingDebit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.totals.closingCredit)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </SectionCard>

            <BalanceCheckBanner label="Closing Difference" difference={data.difference.closing} okLabel="₹0.00 - Trial Balance is balanced" />
          </>
        )}
      </div>

      {data && rows.length > 0 && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Trial Balance" rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr className="border-b border-black">
                  <th className="py-1 text-left">Account</th>
                  <th className="py-1 text-right">Opening Dr</th>
                  <th className="py-1 text-right">Opening Cr</th>
                  <th className="py-1 text-right">Period Dr</th>
                  <th className="py-1 text-right">Period Cr</th>
                  <th className="py-1 text-right">Closing Dr</th>
                  <th className="py-1 text-right">Closing Cr</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.ledgerId} className="border-b border-gray-300">
                    <td className="py-0.5">{row.ledgerName}</td>
                    <td className="py-0.5 text-right">{row.openingDebit ? formatCurrency(row.openingDebit) : ""}</td>
                    <td className="py-0.5 text-right">{row.openingCredit ? formatCurrency(row.openingCredit) : ""}</td>
                    <td className="py-0.5 text-right">{row.periodDebit ? formatCurrency(row.periodDebit) : ""}</td>
                    <td className="py-0.5 text-right">{row.periodCredit ? formatCurrency(row.periodCredit) : ""}</td>
                    <td className="py-0.5 text-right">{row.closingDebit ? formatCurrency(row.closingDebit) : ""}</td>
                    <td className="py-0.5 text-right">{row.closingCredit ? formatCurrency(row.closingCredit) : ""}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-black font-bold">
                  <td className="py-1">Total</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.openingDebit)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.openingCredit)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.periodDebit)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.periodCredit)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.closingDebit)}</td>
                  <td className="py-1 text-right">{formatCurrency(data.totals.closingCredit)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-2 text-[10px]">Difference: {formatCurrency(Math.abs(data.difference.closing))} {data.balanced ? "(Balanced)" : "(MISMATCH)"}</div>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}
